import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
    MdArrowBack,
    MdSearch,
    MdFileDownload,
    MdFilterList,
    MdPeople,
    MdDescription,
    MdCurrencyRupee
} from 'react-icons/md';
import styles from './ProjectVendorDetails.module.css';
import Loader from '../components/common/Loader';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import NoData from '../components/common/NoData';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_PROJECT_VENDOR_PAYMENT_REPORT_API, GET_ALL_VENDORS_API } from '../config/API';
import { toast } from 'react-toastify';

const ProjectVendorDetails = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Initial projectName from search params, will be updated by API
    const [projectName, setProjectName] = useState(searchParams.get('projectName') || 'Project Details');

    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([]);
    const [projectSummary, setProjectSummary] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (selectedVendor?.value) {
                queryParams += `&vendorId=${selectedVendor.value}`;
            }

            const response = await authFetch(
                `${GET_PROJECT_VENDOR_PAYMENT_REPORT_API(id)}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                setProjectSummary(result.projectSummary);
                if (result.projectDetails?.name) {
                    setProjectName(result.projectDetails.name);
                }
                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                setReports([]);
                setProjectSummary(null);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching project vendor report:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (selectedVendor?.value) {
                queryParams += `&vendorName=${selectedVendor.value}`;
            }

            const response = await authFetch(`${GET_PROJECT_VENDOR_PAYMENT_REPORT_API(id)}${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    useEffect(() => {
        fetchReports();
    }, [id, currentPage, debouncedSearch, selectedVendor, limit]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className={styles['purchase-orders-container']}>
            <div className={styles['page-header']}>
                <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/reports/project-summary')} className="back-btn">
                    <MdArrowBack size={20} />
                </button>
                <div className={styles['title-section']}>
                    <h1>{projectName}</h1>
                    <div className={styles['page-subtitle']}>
                        Vendor Audit & Financial Disbursement Registry
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExportCSV}
                    >
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Vendor Name</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_VENDORS_API}
                            mapResponse={(v) => ({
                                value: v.id,
                                label: v.vendorName
                            })}
                            value={selectedVendor}
                            onChange={(option) => setSelectedVendor(option)}
                            placeholder="Select Vendor"
                            isClearable
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setSelectedVendor(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Stats Grid */}
            <div className={styles['stats-grid']}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdPeople />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total Vendors</span>
                    <span className={styles['stat-value']}>{projectSummary?.totalVendors || 0}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total PO Amount</span>
                    <span className={styles['stat-value']}>{formatCurrency(projectSummary?.totalAmount)}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Paid to Date</span>
                    <span className={`${styles['stat-value']} ${styles['amount-paid']}`}>{formatCurrency(projectSummary?.totalPaidAmount)}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['red']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Remaining Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-remaining']}`}>{formatCurrency(projectSummary?.totalRemainingAmount)}</span>
                </div>
            </div>

            {/* Table Card */}
            <div className={styles['card-container-po']}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className={styles['table-container']}>
                    <table>
                        <thead>
                            <tr>
                                <th>Sn</th>
                                <th>Vendor Name</th>
                                <th>Company Name</th>
                                <th className={styles['text-center']}>Total POs</th>
                                <th className={styles['text-right']}>Total Amount</th>
                                <th className={styles['text-right']}>Paid Amount</th>
                                <th className={styles['text-right']}>Remaining</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <Loader text="Fetching vendor details..." />
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <NoData title="No Vendors Found" message="No vendor data available for this project." />
                                    </td>
                                </tr>
                            ) : (
                                reports.map((vendor, index) => (
                                    <tr key={vendor.vendorId || index}>
                                        <td style={{ color: '#9ca3af', fontWeight: '500' }}>{vendor.sn || index + 1}</td>
                                        <td>
                                            <div className={styles['vendor-cell']}>
                                                <div className={styles['vendor-info']}>
                                                    <span className={styles['vendor-name']}>{vendor.vendorName}</span>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{vendor.vendorCategory}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{vendor.companyName || 'N/A'}</td>
                                        <td className={styles['text-center']}>
                                            <span
                                                className={styles['link-text']}
                                                onClick={() => window.open(`/reports/project/${id}/vendor/${vendor.vendorId}/pos`, '_blank')}
                                            >
                                                {vendor.totalPOs}
                                            </span>
                                        </td>
                                        <td className={`${styles['text-right']} ${styles['amount-cell-grandTotal']}`}>{formatCurrency(vendor.totalAmount)}</td>
                                        <td className={`${styles['text-right']} ${styles['amount-cell-paid']}`}>{formatCurrency(vendor.totalPaidAmount)}</td>
                                        <td className={`${styles['text-right']} ${styles['amount-cell-remaining']}`}>{formatCurrency(vendor.totalRemainingAmount)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={reports.length}
                    entityName="Vendor Report"
                />
            </div>
        </div>
    );
};

export default ProjectVendorDetails;
